/**
 * useReservationSuggestions — list + discover + act on AI-discovered bookable
 * experiences for a trip (the Reservations panel's category tabs).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type {
  TravelReservationSuggestionResponse,
  TravelReservationSuggestionStatus,
} from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

export function useReservationSuggestions(tripId: string | undefined) {
  const feature = useTravelFeature('reservation_scout');
  const queryClient = useQueryClient();
  const enabled = !!tripId && feature.enabled;

  const listQuery = useQuery({
    enabled,
    queryKey: tripId
      ? queryKeys.travel.reservationSuggestions(tripId)
      : queryKeys.travel.disabled('reservationSuggestions'),
    queryFn: () => travelApi.listReservationSuggestions(tripId!),
  });

  const invalidate = () => {
    if (!tripId) return;
    void queryClient.invalidateQueries({
      queryKey: queryKeys.travel.reservationSuggestions(tripId),
    });
  };

  const discoverMut = useMutation({
    mutationFn: () => travelApi.discoverReservationSuggestions(tripId!),
    onSuccess: () => invalidate(),
  });

  const statusMut = useMutation({
    mutationFn: (vars: { suggestionId: string; status: TravelReservationSuggestionStatus }) =>
      travelApi.updateReservationSuggestionStatus(vars.suggestionId, vars.status),
    onSuccess: () => invalidate(),
  });

  const deleteMut = useMutation({
    mutationFn: (suggestionId: string) => travelApi.deleteReservationSuggestion(suggestionId),
    onSuccess: () => invalidate(),
  });

  return {
    featureEnabled: feature.enabled,
    suggestions: (listQuery.data?.suggestions ?? []) as TravelReservationSuggestionResponse[],
    loading: listQuery.isLoading,
    discover: () => discoverMut.mutateAsync(),
    isDiscovering: discoverMut.isPending,
    setStatus: (suggestionId: string, status: TravelReservationSuggestionStatus) =>
      statusMut.mutateAsync({ suggestionId, status }),
    remove: (suggestionId: string) => deleteMut.mutateAsync(suggestionId),
  };
}
